import React, { useState } from 'react';
import { useSWRConfig } from 'swr';
import ButtonWithActions from '../ButtonWithActions/ButtonWithActions';
import { Label } from '../../../common/Label/Label';
import { SCORM_LABELS } from '../../../../lib/constants/labelKeys';
import styles from './ContentFileAdminActions.module.scss';
import ContentConnectDialog from '../ContentConnectDialog/ContentConnectDialog';
import { useDisconnectFile } from '../../../../lib/api/scormApi';
import useHandleAction from '../../../../lib/hooks/useHandleAction';
import { CourseDetailsViewProps } from '../../compound/CourseDetails/CourseDetails.types';
import PureLink from '../../../common/Link/PureLink';

export type ContentFileAdminActionsProps = {
  contentFileId?: string;
  contentFileUrl?: string;
  id: string;
} & Pick<CourseDetailsViewProps, 'connectFileSuccessMessage' | 'disconnectFileSuccessMessage'>;

type ConnectFileActionType = 'connect' | 'disconnect' | 'open-file';

const ContentFileAdminActions = ({
  contentFileId,
  contentFileUrl,
  id,
  connectFileSuccessMessage,
  disconnectFileSuccessMessage,
}: ContentFileAdminActionsProps) => {
  const [connectFileDialog, setConnectFileDialog] = useState<{ id: string; contentFileId?: string } | null>(null);
  const disconnect = useDisconnectFile();
  const { mutate } = useSWRConfig();

  const handleOperationSuccess = () => {
    mutate(([key]: string[]) => key.match(/(\/courses\/)|(\/courses\/(.+)\/actions)/));
    setConnectFileDialog(null);
  };

  const { handleAction: handleDisconnect, isPending } = useHandleAction({
    action: disconnect,
    onSuccess: handleOperationSuccess,
    successMessage: disconnectFileSuccessMessage ?? '',
  });

  const handleExtendableAction = (type: ConnectFileActionType) => {
    if (type === 'connect') {
      setConnectFileDialog({ id, contentFileId });
    } else if (type === 'disconnect') {
      handleDisconnect({ id, fileId: contentFileId ?? '' });
    }
  };

  return (
    <>
      <ButtonWithActions
        variant="outlined"
        actions={[
          {
            label: <Label name={SCORM_LABELS[contentFileId ? 'RECONNECT_FILE' : 'CONNECT_FILE']} />,
            value: 'connect',
          },
          ...(contentFileId
            ? [{ label: <Label name={SCORM_LABELS.DISCONNECT_FILE} />, value: 'disconnect' as const }]
            : []),
          ...(contentFileUrl
            ? [
                {
                  label: (
                    <PureLink href={contentFileUrl} className={styles.contentFileAnchor}>
                      <Label name={SCORM_LABELS.OPEN_FILE} />
                    </PureLink>
                  ),
                  value: 'open-file' as const,
                },
              ]
            : []),
        ]}
        classNames={{
          wrapper: styles.connectFileButton,
          mainButton: styles.connectFileMainButton,
          dropdownItem: styles.dropdownItem,
          singleButton: styles.connectFileMainButton,
        }}
        onAction={handleExtendableAction}
        isLoading={isPending}
      />
      {connectFileDialog && (
        <ContentConnectDialog
          onClose={handleOperationSuccess}
          moduleId={connectFileDialog.id}
          successMessage={connectFileSuccessMessage}
          initialValue={connectFileDialog.contentFileId}
        />
      )}
    </>
  );
};

export default ContentFileAdminActions;
